import { createAsyncThunk } from "@reduxjs/toolkit";

import * as checkoutAPI from "../../../api/checkout";

/**
 * Return type for loadEmailAddress thunk.
 */
interface LoadEmailAddressResult {
    email: string | null;
}

/**
 * Load cached basket email address.
 *
 * Retrieves the email address associated with the basket from a previous
 * checkout session. This is typically used for guest checkout to pre-fill
 * the email field if the user has checked out before.
 *
 * @returns {Promise<LoadEmailAddressResult>} Promise resolving to email address or null
 *
 * @example
 * // Load cached email on checkout page mount
 * const result = await dispatch(loadEmailAddress()).unwrap();
 * if (result.email) {
 *   console.log("Pre-filled email:", result.email);
 * }
 *
 * @sideEffects
 * - Updates `form.guest_email` with cached email if present via extraReducers
 * - Logs to console in debug mode
 */
export const loadEmailAddress = createAsyncThunk(
    "checkout/loadEmailAddress",
    async (): Promise<LoadEmailAddressResult> => {
        console.debug("Loading cached basket email address...");
        const data = await checkoutAPI.loadEmailAddress();

        if (data.owner_email) {
            console.debug(
                `Loaded cached basket email address: ${data.owner_email}.`,
            );
            return { email: data.owner_email };
        } else {
            console.debug(`No email address found for basket.`);
            return { email: null };
        }
    },
);
